/**
 * FOS Facet Filters Component
 *
 * Refined filter sections with:
 * - Clean visual hierarchy
 * - Clear active states
 * - Compact but readable layout
 * - Semantic color coding for outcomes
 */

import React, { useState } from 'react';
import { FOSFacets, FOSSearchParams } from '../../../utils/api';
import { Skeleton } from '../../ui/skeleton';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import IconWrapper from '../../IconWrapper';
import { FiChevronRight } from 'react-icons/fi';
import { cn } from '../../../lib/utils';

interface FOSFacetFiltersProps {
  facets: FOSFacets | undefined;
  isLoading: boolean;
  activeFilters: Partial<FOSSearchParams>;
  onFilterChange: (filters: Partial<FOSSearchParams>) => void;
}

interface FilterSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  activeCount?: number;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  isOpen,
  onToggle,
  children,
  activeCount = 0,
}) => (
  <Collapsible open={isOpen} onOpenChange={onToggle}>
    <CollapsibleTrigger className={cn(
      "flex w-full items-center justify-between px-4 py-3 text-sm font-medium",
      "text-foreground/80 hover:text-foreground",
      "transition-colors duration-150"
    )}>
      <div className="flex items-center gap-2">
        <span>{title}</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
            {activeCount}
          </span>
        )}
      </div>
      <IconWrapper
        icon={FiChevronRight}
        className={cn(
          'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
          isOpen && 'rotate-90'
        )}
      />
    </CollapsibleTrigger>
    <CollapsibleContent className="px-4 pb-3 pt-1">{children}</CollapsibleContent>
  </Collapsible>
);

const outcomeLabels: Record<string, string> = {
  upheld: 'Upheld',
  not_upheld: 'Not Upheld',
  partially_upheld: 'Partially Upheld',
  Upheld: 'Upheld',
  'Not Upheld': 'Not Upheld',
  'Partially Upheld': 'Partially Upheld',
};

// Semantic colors for outcome types
const outcomeColors: Record<string, string> = {
  upheld: 'text-emerald-600',
  not_upheld: 'text-muted-foreground',
  partially_upheld: 'text-amber-600',
  Upheld: 'text-emerald-600',
  'Not Upheld': 'text-muted-foreground',
  'Partially Upheld': 'text-amber-600',
};

const compensationRangeLabels: Record<string, string> = {
  none: 'No award',
  '0-500': '£0 – £500',
  '500-1000': '£500 – £1k',
  '1000-5000': '£1k – £5k',
  '5000-10000': '£5k – £10k',
  '10000+': '£10k+',
};

// Filter item component for consistency
interface FilterItemProps {
  id: string;
  label: string;
  count: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
  colorClass?: string;
}

const FilterItem: React.FC<FilterItemProps> = ({
  id,
  label,
  count,
  checked,
  onChange,
  colorClass,
}) => (
  <div className={cn(
    "flex items-center gap-2.5 py-1 px-1 -mx-1 rounded",
    "transition-colors duration-150",
    checked && "bg-primary/5"
  )}>
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
    />
    <Label
      htmlFor={id}
      className="flex-1 flex items-center justify-between text-sm font-normal cursor-pointer"
    >
      <span className={cn(
        "truncate",
        checked ? "text-foreground font-medium" : colorClass || "text-foreground/80"
      )}>
        {label}
      </span>
      <span className={cn(
        "text-xs tabular-nums flex-shrink-0 ml-2",
        checked ? "text-primary font-medium" : "text-muted-foreground/70"
      )}>
        {count.toLocaleString()}
      </span>
    </Label>
  </div>
);

const FOSFacetFilters: React.FC<FOSFacetFiltersProps> = ({
  facets,
  isLoading,
  activeFilters,
  onFilterChange,
}) => {
  const [openSections, setOpenSections] = useState({
    outcome: true,
    product: true,
    company: false,
    compensation: false,
    year: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!facets) {
    return null;
  }

  // Calculate active filter counts
  const activeOutcomeCount = activeFilters.outcome ? 1 : 0;
  const activeProductCount = activeFilters.product_category ? 1 : 0;
  const activeCompanyCount = activeFilters.company ? 1 : 0;
  const activeCompensationCount = (activeFilters.min_compensation !== undefined || activeFilters.max_compensation !== undefined) ? 1 : 0;
  const activeYearCount = activeFilters.year ? 1 : 0;

  return (
    <div className="divide-y divide-border/40">
      {/* Outcome Filter */}
      <FilterSection
        title="Outcome"
        isOpen={openSections.outcome}
        onToggle={() => toggleSection('outcome')}
        activeCount={activeOutcomeCount}
      >
        <div className="space-y-0.5">
          {Object.entries(facets.outcomes).map(([outcome, count]) => (
            <FilterItem
              key={outcome}
              id={`outcome-${outcome}`}
              label={outcomeLabels[outcome] || outcome}
              count={count}
              checked={activeFilters.outcome === outcome}
              onChange={(checked) =>
                onFilterChange({
                  outcome: checked ? outcome : undefined,
                })
              }
              colorClass={outcomeColors[outcome]}
            />
          ))}
        </div>
      </FilterSection>

      {/* Product Category Filter */}
      <FilterSection
        title="Product"
        isOpen={openSections.product}
        onToggle={() => toggleSection('product')}
        activeCount={activeProductCount}
      >
        <ScrollArea className="h-40">
          <div className="space-y-0.5 pr-3">
            {Object.entries(facets.products)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 15)
              .map(([product, count]) => (
                <FilterItem
                  key={product}
                  id={`product-${product}`}
                  label={product}
                  count={count}
                  checked={activeFilters.product_category === product}
                  onChange={(checked) =>
                    onFilterChange({
                      product_category: checked ? product : undefined,
                    })
                  }
                />
              ))}
          </div>
        </ScrollArea>
      </FilterSection>

      {/* Company Filter */}
      <FilterSection
        title="Company"
        isOpen={openSections.company}
        onToggle={() => toggleSection('company')}
        activeCount={activeCompanyCount}
      >
        <ScrollArea className="h-40">
          <div className="space-y-0.5 pr-3">
            {Object.entries(facets.companies)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 15)
              .map(([company, count]) => (
                <FilterItem
                  key={company}
                  id={`company-${company}`}
                  label={company}
                  count={count}
                  checked={activeFilters.company === company}
                  onChange={(checked) =>
                    onFilterChange({
                      company: checked ? company : undefined,
                    })
                  }
                />
              ))}
          </div>
        </ScrollArea>
      </FilterSection>

      {/* Compensation Filter */}
      <FilterSection
        title="Award"
        isOpen={openSections.compensation}
        onToggle={() => toggleSection('compensation')}
        activeCount={activeCompensationCount}
      >
        <div className="space-y-0.5">
          {Object.entries(facets.compensation_ranges)
            .sort((a, b) => {
              const order = ['none', '0-500', '500-1000', '1000-5000', '5000-10000', '10000+'];
              return order.indexOf(a[0]) - order.indexOf(b[0]);
            })
            .map(([range, count]) => {
              // Convert range to min/max for filtering
              const getFilterFromRange = (r: string) => {
                switch (r) {
                  case 'none':
                    return { max_compensation: 0 };
                  case '0-500':
                    return { min_compensation: 0, max_compensation: 500 };
                  case '500-1000':
                    return { min_compensation: 500, max_compensation: 1000 };
                  case '1000-5000':
                    return { min_compensation: 1000, max_compensation: 5000 };
                  case '5000-10000':
                    return { min_compensation: 5000, max_compensation: 10000 };
                  case '10000+':
                    return { min_compensation: 10000 };
                  default:
                    return {};
                }
              };

              return (
                <FilterItem
                  key={range}
                  id={`comp-${range}`}
                  label={compensationRangeLabels[range] || range}
                  count={count}
                  checked={false}
                  onChange={(checked) => {
                    if (checked) {
                      onFilterChange(getFilterFromRange(range));
                    } else {
                      onFilterChange({
                        min_compensation: undefined,
                        max_compensation: undefined,
                      });
                    }
                  }}
                />
              );
            })}
        </div>
      </FilterSection>

      {/* Year Filter */}
      <FilterSection
        title="Year"
        isOpen={openSections.year}
        onToggle={() => toggleSection('year')}
        activeCount={activeYearCount}
      >
        <ScrollArea className="h-28">
          <div className="space-y-0.5 pr-3">
            {Object.entries(facets.years)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .map(([year, count]) => (
                <FilterItem
                  key={year}
                  id={`year-${year}`}
                  label={year}
                  count={count}
                  checked={activeFilters.year === Number(year)}
                  onChange={(checked) =>
                    onFilterChange({
                      year: checked ? Number(year) : undefined,
                    })
                  }
                />
              ))}
          </div>
        </ScrollArea>
      </FilterSection>
    </div>
  );
};

export default FOSFacetFilters;
